import {put, call, all} from 'redux-saga/effects';
import Docket from "../../entities/docket";
import * as Actions from "../actions/docketActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type DocketAction = {
    docketId: string;
    docket: Docket;
    dockets: Docket[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneDocket(action: DocketAction) {
	const session = tabSession.captureGeneration();
	try {
		const docket: Docket = yield call(api.findOne, 'docket', action.docketId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_FETCH_ONE_SUCCESS, docketId: action.docketId, docket, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCKET_FETCH_ONE_FAILURE, docketId: action.docketId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {DocketAction} action
 */
export function* saveDocket(action: DocketAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DOCKET_SAVE saga', action.docket?.id);

		if (action.docket === undefined || action.docket === null) {
			console.error('saveDocket received a null');
			throw new Error('saveDocket received a null');
		}

		if (!(action.docket instanceof Docket)) {
			action.docket = new Docket(action.docket);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.docket.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.docket);
			const addResult = yield call(idb.add, 'docket', action.docket);
			if (addResult.isErr()) {
				console.error(`⮂ saveDocket: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertDocketSave(action.docket, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.DOCKET_SAVE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.DOCKET_SAVE, docket: action.docket, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertDocketSave(action.docket, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.DOCKET_SAVE_FAILURE, docket: action.docket, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {DocketAction} action
 * @param {Docket[]} action.dockets
 */
export function* saveManyDockets(action: DocketAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ DOCKET_SAVE_MANY saga', action.dockets.map(docket => docket.id).join(','));

        if (!Array.isArray(action.dockets)) {
            console.error('saveManyDockets expected an array, received', action.dockets);
            throw new Error('saveManyDockets expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveDocket), under these queue keys
		const dispatchedAddedOns: number[] = action.dockets.map(docket => docket?.addedOn);
		const revertAll = () => action.dockets.map((docket, i) =>
			put(Actions.revertDocketSave(docket, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Docket[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.dockets.length; i++) {
				const docket = action.dockets[i];
				docket.persisted = false;
				ensureOperationId(docket);
				toSave.push(docket);
			}

			const addResult = yield call(idb.add, 'docket', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyDockets: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.DOCKET_SAVE_MANY_FAILURE, dockets: action.dockets, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.DOCKET_SAVE_MANY, dockets: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.DOCKET_SAVE_MANY_FAILURE, dockets: action.dockets, status: 500, err});

			console.error(`Error saving ${action.dockets.length} dockets to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {DocketAction} action
 */
export function* removeDocket(action: DocketAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.docket === undefined || action.docket === null || !(action.docket instanceof Docket)) {
			console.error('removeDocket expected type Docket, received', action.docket);
			reportQueued(false);
			throw new Error('removeDocket expected type Docket');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.docket.status = 'D';
		action.docket.addedOn = newHRTimestamp();
		action.docket.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.docket);
		const addResult = yield call(idb.add, 'docket', action.docket);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeDocket: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.DOCKET_REMOVE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_REMOVE, docket: action.docket});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCKET_REMOVE_FAILURE, docket: action.docket, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Docket.
	*
	* @param {DocketAction} action
	*/
export function* restoreDocket(action: DocketAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreDocket expected getCurrentUserId() to return a value');
        return;
    }

	if (action.docket === undefined || action.docket === null) {
        console.error('restoreDocket expected a Docket, received null');
        throw new Error('restoreDocket expected a Docket, received null');
    }
    else if (!(action.docket instanceof Docket)) action.docket = new Docket(action.docket); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.docket.status = 'U';
		action.docket.addedOn = newHRTimestamp();
		action.docket.addedBy = userId;
		action.docket.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.docket);
		const addResult = yield call(idb.add, 'docket', action.docket);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreDocket: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.DOCKET_RESTORE_FAILURE, docket: action.docket, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.DOCKET_RESTORE, docket: action.docket});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.DOCKET_RESTORE_FAILURE, docket: action.docket, status: 500, err});

		console.error('Error restoring from database', err);
	}
}